import Card from '@/gf/components/Card'
import Spinner from '@/gf/components/Spinner'
import { Table, Tbody, Td, Th, Thead, Tr } from '@/gf/components/Table'
import StoreOrderM from '@/gf/modules/StoreOrder'
import { Link } from 'react-router-dom'
import { Option } from '../../types'
import useCancellationMetrics from '../hooks/useCancellationMetrics'
import useCancellationMetricsChartData from '../hooks/useCancellationMetricsChartData'
import CancellationLineChart from './CancellationLineChart'

interface CancellationDataTypeOption extends Omit<Option, 'id'> {
  id: 'count' | 'percent'
}
const dataTypeOptions: CancellationDataTypeOption[] = [
  {
    id: 'percent',
    display: 'Percent',
  },
  {
    id: 'count',
    display: 'Count',
  },
]

const formatPercent = (percentNumber: number) => `${(percentNumber * 100).toFixed(2)}%`
const getReadableCancelEventReason = (id: string) => {
  const cancelReason = StoreOrderM.CANCEL_REASONS.find((reason) => reason.id === id)
  if (cancelReason) {
    return cancelReason.title
  }
  return 'Unknown cancel reason'
}

const CancellationMetrics = ({
  fromDate,
  toDate,
  selectedStoreId,
  chartPeriodDays,
  chartDataType,
}: {
  fromDate: string
  toDate: string
  selectedStoreId: string
  chartPeriodDays: number
  chartDataType: string
}) => {
  const { loading, error, data } = useCancellationMetrics({ fromDate, toDate, selectedStoreId })
  const {
    loading: chartLoading,
    error: chartError,
    data: chartData,
  } = useCancellationMetricsChartData({
    fromDate,
    toDate,
    selectedStoreId,
    periodDays: chartPeriodDays,
  })

  const dataType = dataTypeOptions.find((option) => option.id === chartDataType)?.id ?? 'percent'

  return (
    <Card title="Cancellation metrics">
      {error ? (
        <Card.Section title="Error fetching data">{`${error.message}`}</Card.Section>
      ) : loading || !data ? (
        <Card.Section>
          <Spinner />
        </Card.Section>
      ) : (
        <>
          <Card.Section>
            <div className="h-80">
              {chartError ? (
                <p className="text-sm text-gray-700">{chartError.message}</p>
              ) : chartLoading || !chartData ? (
                <Spinner />
              ) : (
                <CancellationLineChart data={chartData} dataType={dataType} />
              )}
            </div>
          </Card.Section>
          <Card.Section title="Cancelled overview">
            <div className="text-sm text-gray-700 grid grid-cols-2 gap-2">
              <p>Total store orders:</p>
              <p>{data.allStoreOrdersCount}</p>
              <p>Cancelled store orders:</p>
              <p>{data.cancelledStoreOrdersCount}</p>
              <p>Cancelled store orders:</p>
              <p>{formatPercent(data.cancelledStoreOrdersPercent)}</p>
            </div>
          </Card.Section>
          <Card.Section title="Cancellation reasons">
            <Table className="rounded border border-gray-300">
              <Thead>
                <Tr>
                  <Th>Reason</Th>
                  <Th>Percent</Th>
                  <Th>Count</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.cancelledStoreOrderEventsGroupCount
                  // Sort in descending order by percent
                  .sort((a, b) => b.percent - a.percent)
                  .map((eventGroupCount) => (
                    <Tr key={eventGroupCount.id}>
                      <Td>{getReadableCancelEventReason(eventGroupCount.id)}</Td>
                      <Td>{formatPercent(eventGroupCount.percent)}</Td>
                      <Td>{eventGroupCount.count}</Td>
                    </Tr>
                  ))}
              </Tbody>
            </Table>
          </Card.Section>
          <Card.Section title="Other cancellation reasons">
            <Table className="rounded border border-gray-300 w-full">
              <Thead>
                <Tr>
                  <Th>Reason</Th>
                  <Th>Store order</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.cancelledStoreOrderEventsOtherReason.map(({ details, storeOrderId }) => (
                  <Tr key={storeOrderId}>
                    <Td className="whitespace-normal">{details}</Td>
                    <Td>
                      <Link className="text-blue-500 underline" to={`/orders/${storeOrderId}`}>
                        {storeOrderId}
                      </Link>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Card.Section>
        </>
      )}
    </Card>
  )
}

CancellationMetrics.dataTypeOptions = dataTypeOptions

export default CancellationMetrics
